import * as THREE from 'three';

// ---- Procedural Canvas Textures -------------------------------------------

export const textureCache = new Map<string, THREE.CanvasTexture>();

export function getCachedTexture(key: string, gen: () => THREE.CanvasTexture): THREE.CanvasTexture {
  let tex = textureCache.get(key);
  if (tex) return tex;
  tex = gen();
  textureCache.set(key, tex);
  return tex;
}

/** Sandy brick wall texture — warm beige/tan bricks with mortar lines */
export function makeBrickTexture(): THREE.CanvasTexture {
  return getCachedTexture('brick', () => {
    const s = 512;
    const c = document.createElement('canvas');
    c.width = s; c.height = s;
    const ctx = c.getContext('2d')!;

    // Base sandy color
    ctx.fillStyle = '#b8a07a';
    ctx.fillRect(0, 0, s, s);

    // Draw bricks (scaled for 512px)
    const brickH = 48, brickW = 112, mortarW = 4;
    const colors = ['#c4a87c', '#b89e72', '#a89468', '#c9ad82', '#b09060', '#bca476', '#a8946a'];
    for (let row = 0; row < Math.ceil(s / (brickH + mortarW)); row++) {
      const y = row * (brickH + mortarW);
      const offset = (row % 2) * (brickW / 2 + mortarW / 2);
      for (let col = -1; col < Math.ceil(s / (brickW + mortarW)) + 1; col++) {
        const x = col * (brickW + mortarW) + offset;
        ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];
        ctx.fillRect(x, y, brickW, brickH);
        // Subtle variation within brick — surface noise
        for (let n = 0; n < 12; n++) {
          const nx = x + Math.random() * brickW;
          const ny = y + Math.random() * brickH;
          ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.06})`;
          ctx.fillRect(nx, ny, 4 + Math.random() * 8, 3 + Math.random() * 6);
        }
        // Highlight edge
        ctx.fillStyle = 'rgba(255,255,220,0.1)';
        ctx.fillRect(x, y, brickW, 2);
        ctx.fillRect(x, y, 2, brickH);
        // Shadow edge
        ctx.fillStyle = 'rgba(0,0,0,0.06)';
        ctx.fillRect(x, y + brickH - 2, brickW, 2);
        ctx.fillRect(x + brickW - 2, y, 2, brickH);
      }
    }
    // Mortar lines
    ctx.fillStyle = '#9a8a6e';
    for (let row = 0; row <= Math.ceil(s / (brickH + mortarW)); row++) {
      ctx.fillRect(0, row * (brickH + mortarW) - mortarW / 2, s, mortarW);
    }

    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    return tex;
  });
}

/** Concrete / stucco texture with subtle cracks and color variation */
export function makeConcreteTexture(): THREE.CanvasTexture {
  return getCachedTexture('concrete', () => {
    const s = 256;
    const c = document.createElement('canvas');
    c.width = s; c.height = s;
    const ctx = c.getContext('2d')!;

    ctx.fillStyle = '#a09888';
    ctx.fillRect(0, 0, s, s);

    // Dense noise variation
    for (let i = 0; i < 3000; i++) {
      const x = Math.random() * s, y = Math.random() * s;
      const l = 130 + Math.random() * 50;
      ctx.fillStyle = `rgba(${l},${l - 8},${l - 16},0.2)`;
      ctx.fillRect(x, y, 2 + Math.random() * 4, 2 + Math.random() * 4);
    }

    // Subtle stains
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = `rgba(80,70,55,${0.03 + Math.random() * 0.04})`;
      ctx.beginPath();
      ctx.arc(Math.random() * s, Math.random() * s, 10 + Math.random() * 25, 0, Math.PI * 2);
      ctx.fill();
    }

    // Cracks
    ctx.strokeStyle = 'rgba(80,70,60,0.18)';
    ctx.lineWidth = 0.8;
    for (let i = 0; i < 5; i++) {
      ctx.beginPath();
      let cx = Math.random() * s, cy = Math.random() * s;
      ctx.moveTo(cx, cy);
      for (let j = 0; j < 8; j++) {
        cx += (Math.random() - 0.5) * 30;
        cy += Math.random() * 20;
        ctx.lineTo(cx, cy);
      }
      ctx.stroke();
    }

    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    return tex;
  });
}

/** Sandy ground / dirt texture */
export function makeSandTexture(): THREE.CanvasTexture {
  return getCachedTexture('sand', () => {
    const s = 512;
    const c = document.createElement('canvas');
    c.width = s; c.height = s;
    const ctx = c.getContext('2d')!;

    ctx.fillStyle = '#c4a868';
    ctx.fillRect(0, 0, s, s);

    // Dense sand grain noise
    for (let i = 0; i < 8000; i++) {
      const x = Math.random() * s, y = Math.random() * s;
      const v = Math.random();
      const r = 175 + v * 40, g = 150 + v * 35, b = 80 + v * 30;
      ctx.fillStyle = `rgba(${r},${g},${b},0.35)`;
      const sz = 1 + Math.random() * 3;
      ctx.fillRect(x, y, sz, sz);
    }

    // Pebbles / small stones
    for (let i = 0; i < 40; i++) {
      const px = Math.random() * s, py = Math.random() * s;
      const pr = 2 + Math.random() * 4;
      ctx.fillStyle = `rgba(${140 + Math.random() * 40},${120 + Math.random() * 30},${70 + Math.random() * 30},0.3)`;
      ctx.beginPath();
      ctx.ellipse(px, py, pr, pr * 0.7, Math.random() * Math.PI, 0, Math.PI * 2);
      ctx.fill();
    }

    // Darker patches / footprint impressions
    for (let i = 0; i < 12; i++) {
      ctx.fillStyle = `rgba(120,100,60,${0.04 + Math.random() * 0.05})`;
      ctx.beginPath();
      ctx.arc(Math.random() * s, Math.random() * s, 8 + Math.random() * 20, 0, Math.PI * 2);
      ctx.fill();
    }

    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    return tex;
  });
}

/** Wood texture for crates */
export function makeWoodTexture(): THREE.CanvasTexture {
  return getCachedTexture('wood', () => {
    const s = 256;
    const c = document.createElement('canvas');
    c.width = s; c.height = s;
    const ctx = c.getContext('2d')!;

    ctx.fillStyle = '#8b6f47';
    ctx.fillRect(0, 0, s, s);

    // Wood grain (horizontal lines with more detail)
    for (let y = 0; y < s; y++) {
      const v = Math.sin(y * 0.08) * 12 + Math.sin(y * 0.3) * 5 + Math.random() * 6;
      ctx.fillStyle = `rgba(${100 + v},${75 + v},${40 + v},0.45)`;
      ctx.fillRect(0, y, s, 1);
    }

    // Knots with rings
    for (let i = 0; i < 3; i++) {
      const kx = 20 + Math.random() * (s - 40), ky = 20 + Math.random() * (s - 40);
      for (let r = 12; r > 0; r -= 2) {
        ctx.fillStyle = `rgba(${70 + r * 3},${45 + r * 2},${20 + r},${0.15 + (12 - r) * 0.02})`;
        ctx.beginPath();
        ctx.ellipse(kx, ky, r, r * 1.3, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Plank divider lines with depth
    ctx.strokeStyle = 'rgba(40,25,10,0.4)';
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(s / 2, 0); ctx.lineTo(s / 2, s); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, s / 2); ctx.lineTo(s, s / 2); ctx.stroke();
    // Highlight next to divider
    ctx.strokeStyle = 'rgba(180,150,100,0.15)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(s / 2 + 2, 0); ctx.lineTo(s / 2 + 2, s); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, s / 2 + 2); ctx.lineTo(s, s / 2 + 2); ctx.stroke();

    // Nail heads at corners of planks
    const nailPositions = [[s / 4, s / 4], [3 * s / 4, s / 4], [s / 4, 3 * s / 4], [3 * s / 4, 3 * s / 4]];
    for (const [nx, ny] of nailPositions) {
      ctx.fillStyle = 'rgba(60,60,60,0.5)';
      ctx.beginPath();
      ctx.arc(nx, ny, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(180,180,180,0.2)';
      ctx.beginPath();
      ctx.arc(nx - 1, ny - 1, 1.5, 0, Math.PI * 2);
      ctx.fill();
    }

    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    return tex;
  });
}

/** Metal texture for catwalks and containers */
export function makeMetalTexture(): THREE.CanvasTexture {
  return getCachedTexture('metal', () => {
    const s = 256;
    const c = document.createElement('canvas');
    c.width = s; c.height = s;
    const ctx = c.getContext('2d')!;

    ctx.fillStyle = '#6a7580';
    ctx.fillRect(0, 0, s, s);

    // Brushed metal streaks (horizontal)
    for (let y = 0; y < s; y++) {
      const v = Math.random() * 20 - 10;
      ctx.fillStyle = `rgba(${115 + v},${125 + v},${138 + v},0.25)`;
      ctx.fillRect(0, y, s, 1);
    }

    // Panel seams
    ctx.strokeStyle = 'rgba(40,50,60,0.3)';
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(0, s / 2); ctx.lineTo(s, s / 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s / 2, 0); ctx.lineTo(s / 2, s); ctx.stroke();

    // Rivets / bolt pattern
    const rivetPositions = [
      [20, 20], [s - 20, 20], [20, s - 20], [s - 20, s - 20],
      [s / 2, 20], [s / 2, s - 20], [20, s / 2], [s - 20, s / 2],
    ];
    for (const [rx, ry] of rivetPositions) {
      ctx.fillStyle = 'rgba(50,60,70,0.5)';
      ctx.beginPath();
      ctx.arc(rx, ry, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(160,170,180,0.3)';
      ctx.beginPath();
      ctx.arc(rx - 1, ry - 1, 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Scratches
    ctx.strokeStyle = 'rgba(180,190,200,0.12)';
    ctx.lineWidth = 0.5;
    for (let i = 0; i < 12; i++) {
      ctx.beginPath();
      const sx = Math.random() * s, sy = Math.random() * s;
      ctx.moveTo(sx, sy);
      ctx.lineTo(sx + (Math.random() - 0.5) * 60, sy + (Math.random() - 0.5) * 20);
      ctx.stroke();
    }

    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    return tex;
  });
}

/** Wall trim / ornate detail texture */
export function makeTrimTexture(): THREE.CanvasTexture {
  return getCachedTexture('trim', () => {
    const w = 512, h = 64;
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d')!;

    ctx.fillStyle = '#b8a480';
    ctx.fillRect(0, 0, w, h);

    // Decorative bands
    ctx.fillStyle = '#a09070';
    ctx.fillRect(0, 8, w, 3);
    ctx.fillRect(0, h - 11, w, 3);

    // Repeated diamond/zigzag motif
    ctx.strokeStyle = '#8a7a60';
    ctx.lineWidth = 2;
    for (let x = 0; x < w; x += 32) {
      ctx.beginPath();
      ctx.moveTo(x, h / 2 - 12);
      ctx.lineTo(x + 16, h / 2);
      ctx.lineTo(x, h / 2 + 12);
      ctx.lineTo(x + 16, h / 2);
      ctx.lineTo(x + 32, h / 2 - 12);
      ctx.stroke();
    }

    // Top/bottom border highlight
    ctx.fillStyle = 'rgba(255,240,200,0.25)';
    ctx.fillRect(0, 0, w, 2);
    ctx.fillStyle = 'rgba(0,0,0,0.18)';
    ctx.fillRect(0, h - 2, w, 2);

    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    return tex;
  });
}

/** Dark stucco for outer wall exteriors */
export function makeDarkWallTexture(): THREE.CanvasTexture {
  return getCachedTexture('darkwall', () => {
    const s = 256;
    const c = document.createElement('canvas');
    c.width = s; c.height = s;
    const ctx = c.getContext('2d')!;

    ctx.fillStyle = '#8a7e6e';
    ctx.fillRect(0, 0, s, s);

    // Dense stucco noise
    for (let i = 0; i < 2000; i++) {
      const x = Math.random() * s, y = Math.random() * s;
      const l = 110 + Math.random() * 40;
      ctx.fillStyle = `rgba(${l},${l - 10},${l - 20},0.2)`;
      ctx.fillRect(x, y, 2 + Math.random() * 4, 2 + Math.random() * 4);
    }

    // Water stain streaks
    for (let i = 0; i < 3; i++) {
      ctx.strokeStyle = `rgba(70,60,50,${0.04 + Math.random() * 0.03})`;
      ctx.lineWidth = 3 + Math.random() * 5;
      ctx.beginPath();
      let sx = Math.random() * s;
      ctx.moveTo(sx, 0);
      for (let j = 0; j < s; j += 10) {
        sx += (Math.random() - 0.5) * 8;
        ctx.lineTo(sx, j);
      }
      ctx.stroke();
    }

    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    return tex;
  });
}

/** Barrel/accent rusty metal texture */
export function makeRustyTexture(): THREE.CanvasTexture {
  return getCachedTexture('rusty', () => {
    const s = 256;
    const c = document.createElement('canvas');
    c.width = s; c.height = s;
    const ctx = c.getContext('2d')!;

    ctx.fillStyle = '#8b4030';
    ctx.fillRect(0, 0, s, s);

    // Rust patches
    for (let i = 0; i < 1200; i++) {
      const x = Math.random() * s, y = Math.random() * s;
      const r = 120 + Math.random() * 50, g = 50 + Math.random() * 30, b = 20 + Math.random() * 30;
      ctx.fillStyle = `rgba(${r},${g},${b},0.3)`;
      ctx.fillRect(x, y, 2 + Math.random() * 5, 2 + Math.random() * 5);
    }

    // Larger rust blotches
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = `rgba(${100 + Math.random() * 40},${30 + Math.random() * 20},${10 + Math.random() * 20},0.15)`;
      ctx.beginPath();
      ctx.arc(Math.random() * s, Math.random() * s, 10 + Math.random() * 20, 0, Math.PI * 2);
      ctx.fill();
    }

    // Rust streaks
    ctx.strokeStyle = 'rgba(100,40,20,0.15)';
    ctx.lineWidth = 3;
    for (let i = 0; i < 5; i++) {
      ctx.beginPath();
      let rx = Math.random() * s;
      ctx.moveTo(rx, 0);
      for (let j = 0; j < s; j += 15) {
        rx += (Math.random() - 0.5) * 10;
        ctx.lineTo(rx, j);
      }
      ctx.stroke();
    }

    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    return tex;
  });
}
